import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  MapContainer,
  TileLayer,
  LayersControl,
  ScaleControl,
  GeoJSON,
} from 'react-leaflet';

import { divIcon } from 'leaflet';
import { renderToStaticMarkup } from 'react-dom/server';

import MacrobenthosLayer from 'components/EcoEnvironmentalSurvey/LeafletMap/MacrobenthosLayer';
import WaterQualityLayer from 'components/EcoEnvironmentalSurvey/LeafletMap/WaterQualityLayer';
import Menu from 'components/EcoEnvironmentalSurvey/LeafletMap/Menu';

import { baseMapList } from 'data/common';

import macrobenthosIconImg from 'img/icon/macrobenthos.svg';
import waterQualityIconImg from 'img/icon/water-quality.svg';

const INTERTIDAL_TOPOGRAPHY_LEGEND = [
  { label: '-5 m', color: 'rgb(0, 80, 200)' },
  { label: '-3 m', color: 'rgb(0, 120, 255)' },
  { label: '-1 m', color: 'rgb(0, 200, 150)' },
  { label: '0 m', color: 'rgb(50, 255, 100)' },
  { label: '1 m', color: 'rgb(255, 255, 0)' },
  { label: '2 m', color: 'rgb(255, 180, 0)' },
  { label: '3 m', color: 'rgb(200, 100, 0)' },
];

const BIRD_SURVEY_GRID_LEGEND = [
  { label: '1 - 10', color: '#ffffcc' },
  { label: '10 - 100', color: '#ffeda0' },
  { label: '100 - 1,000', color: '#fed976' },
  { label: '1,000 - 5,000', color: '#feb24c' },
  { label: '5,000 - 10,000', color: '#fd8d3c' },
  { label: '10,000 - 50,000', color: '#fc4e2a' },
  { label: '50,000 - 100,000', color: '#e31a1c' },
  { label: '100,000+', color: '#bd0026' },
];

const BIRD_SURVEY_CLEAN_BASE_MAP = {
  id: 'openstreetmap',
  title: 'OpenStreetMap',
  url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright" target="blank">OpenStreetMap contributors</a>',
};

const getBirdSurveyGridColor = (counts = 0) => {
  if (counts >= 100000) return '#bd0026';
  if (counts >= 50000) return '#e31a1c';
  if (counts >= 10000) return '#fc4e2a';
  if (counts >= 5000) return '#fd8d3c';
  if (counts >= 1000) return '#feb24c';
  if (counts >= 100) return '#fed976';
  if (counts >= 10) return '#ffeda0';
  if (counts >= 1) return '#ffffcc';
  return '#ffffff';
};

const escapePopupValue = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (char) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[char])
  );

const birdSurveyGridStyle = (feature) => {
  const counts = Number(feature?.properties?.counts || 0);

  return {
    color: '#4f4f4f',
    weight: 1,
    opacity: 0.75,
    fillColor: getBirdSurveyGridColor(counts),
    fillOpacity: 0.58,
  };
};

const handleBirdSurveyGridPopup = (feature, layer) => {
  const properties = feature?.properties || {};
  const counts = Number(properties.counts || 0).toLocaleString();
  const rawGridLevel = properties.current_grid_level || '-';
  const gridLevel = escapePopupValue(String(rawGridLevel).replace('grid_', ''));
  const gridCode = escapePopupValue(properties.current_grid || '-');

  layer.bindPopup(`
    <div class="c-map__popup c-map__popup--bird-survey">
      <div class="c-map__table">
        <div class="c-map__row">
          <div class="c-map__col c-map__col--title">資料筆數</div>
          <div class="c-map__col c-map__col--count"><strong>${counts}</strong></div>
        </div>
        <div class="c-map__row">
          <div class="c-map__col c-map__col--title">網格大小</div>
          <div class="c-map__col">${gridLevel} km</div>
        </div>
        <div class="c-map__row">
          <div class="c-map__col c-map__col--title">網格編號</div>
          <div class="c-map__col">${gridCode}</div>
        </div>
      </div>
    </div>
  `);
};

function Content(props) {
  const { intertidalYear = 2022, birdSurveyMapData = null } = props;
  const { pathname } = useLocation();
  const paths = pathname.split('/');
  const currentPath =
    paths[paths.length - 1] === ''
      ? paths[paths.length - 2]
      : paths[paths.length - 1];
  const isIntertidalTopographyPage = currentPath === 'intertidal-topography';
  const isBirdSurveyPage = currentPath === 'bird-survey';
  const hasBirdSurveyMapData =
    birdSurveyMapData?.type === 'FeatureCollection' &&
    Array.isArray(birdSurveyMapData.features);
  const isObservationPointPage = ['macrobenthos', 'water-quality'].includes(
    currentPath
  );
  const prioritizedBaseMapIds = ['orthophoto', 'spot'];
  const activeBaseMapList = isIntertidalTopographyPage
    ? [
        ...prioritizedBaseMapIds
          .map((id) => baseMapList.find((item) => item.id === id))
          .filter(Boolean),
        ...baseMapList.filter(
          (item) => !prioritizedBaseMapIds.includes(item.id)
        ),
      ]
    : isBirdSurveyPage
    ? [BIRD_SURVEY_CLEAN_BASE_MAP, ...baseMapList]
    : baseMapList;
  const [baseMap, setBaseMap] = useState(
    isIntertidalTopographyPage
      ? 'orthophoto'
      : isBirdSurveyPage
      ? BIRD_SURVEY_CLEAN_BASE_MAP.id
      : 'emap98'
  );
  const intertidalTopoTileUrl = `/tiles/intertidal-topo-${intertidalYear}/{z}/{x}/{y}.png`;
  const intertidalTopoAttribution =
    '陳毅青 Yi-Chin Chen｜' +
    '國立彰化師範大學地理學系 ' +
    'Department of Geography, National Changhua University of Education';

  const iconMarkup = (img, title) =>
    renderToStaticMarkup(
      <div className="c-map__marker" data-type={title}>
        <img
          src={img}
          alt={title}
          className="e-img e-img--contain c-map__icon"
        />
      </div>
    );
  const mapIcon = (img, title) =>
    divIcon({
      html: iconMarkup(img, title),
    });

  const handleCenter = () => {
    switch (currentPath) {
      case 'macrobenthos':
        return { center: [23.927627352463173, 120.3126043504604], zoom: 12 };
      case 'water-quality':
        return { center: [23.95420348627543, 120.32102450000002], zoom: 12 };
      case 'intertidal-topography':
        return { center: [24.03182, 120.347139], zoom: 11 };
      case 'bird-survey':
        return { center: [23.95420348627543, 120.32102450000002], zoom: 11 };
      default:
        return { center: [23.95420348627543, 120.32102450000002], zoom: 13 };
    }
  };

  return (
    <>
      <div className="l-macrobenthos__map u-section">
        {isObservationPointPage ? (
          <h2 className="c-title">觀測點位</h2>
        ) : isIntertidalTopographyPage ? (
          <h2 className="c-title">地圖</h2>
        ) : (
          <></>
        )}

        <MapContainer
          id="leafletmap"
          className="c-map"
          center={handleCenter().center}
          zoom={handleCenter().zoom}
          scrollWheelZoom={true}
          doubleClickZoom={false}
        >
          <Menu
            baseMapList={activeBaseMapList}
            baseMap={baseMap}
            setBaseMap={setBaseMap}
            showFilter={isObservationPointPage}
          />
          {/* <TileLayer
            attribution='&copy; <a href="https://wmts.nlsc.gov.tw/wmts/" target="blank">國土測繪圖資服務雲</a>'
            url="https://wmts.nlsc.gov.tw/wmts/EMAP98/default/GoogleMapsCompatible/{z}/{y}/{x}"
          /> */}
          {isObservationPointPage && (
            <LayersControl>
              <MacrobenthosLayer
                currentPath={currentPath}
                mapIcon={mapIcon(macrobenthosIconImg, 'macrobenthos')}
              />
              <WaterQualityLayer
                currentPath={currentPath}
                mapIcon={mapIcon(waterQualityIconImg, 'water-quality')}
              />
            </LayersControl>
          )}
          {activeBaseMapList.map((item) => {
            const { id, url, attribution } = item;
            return (
              baseMap === id && (
                <TileLayer
                  key={id}
                  url={url}
                  attribution={attribution}
                  zIndex={100}
                />
              )
            );
          })}
          {isIntertidalTopographyPage && (
            <TileLayer
              key={`intertidal-topo-${intertidalYear}`}
              url={intertidalTopoTileUrl}
              attribution={intertidalTopoAttribution}
              opacity={1}
              tms={true}
              minNativeZoom={8}
              maxNativeZoom={16}
              zIndex={300}
            />
          )}
          {isBirdSurveyPage && hasBirdSurveyMapData && (
            <GeoJSON
              key={`bird-survey-grid-${
                birdSurveyMapData.features?.length || 0
              }`}
              data={birdSurveyMapData}
              style={birdSurveyGridStyle}
              onEachFeature={handleBirdSurveyGridPopup}
            />
          )}
          {isIntertidalTopographyPage && (
            <div className="c-map__legend">
              <h3 className="c-map__legend-title">高度圖例 (m)</h3>
              <ul className="c-map__legend-list">
                {INTERTIDAL_TOPOGRAPHY_LEGEND.map((item) => (
                  <li key={item.label} className="c-map__legend-item">
                    <span
                      className={`c-map__legend-swatch ${
                        item.isNoValue ? 'is-empty' : ''
                      }`}
                      style={{ backgroundColor: item.color }}
                      aria-hidden="true"
                    />
                    <span className="c-map__legend-label">{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {isBirdSurveyPage && (
            <div className="c-map__legend">
              <h3 className="c-map__legend-title">資料筆數</h3>
              <ul className="c-map__legend-list">
                {BIRD_SURVEY_GRID_LEGEND.map((item) => (
                  <li key={item.label} className="c-map__legend-item">
                    <span
                      className="c-map__legend-swatch"
                      style={{ backgroundColor: item.color }}
                      aria-hidden="true"
                    />
                    <span className="c-map__legend-label">{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <ScaleControl position="bottomleft" imperial={false} />
        </MapContainer>
      </div>
    </>
  );
}

export default Content;
